import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';
import { Globe, Sparkles, ExternalLink, Server, Calendar, Activity, Wind } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

type Project = {
  title: string;
  status?: string;
  description: string;
  type: string;
  icon: LucideIcon;
  color: string;
  bg: string;
  link: string | null;
};

const projects: Project[] = [
  {
    title: 'ProductByte',
    description: 'An AI commerce platform turning products into campaign-ready creatives, publishing them across channels, and keeping product feeds in sync. Full platform & infrastructure development.',
    type: 'Full Stack Development',
    icon: Sparkles,
    color: 'text-purple-500',
    bg: 'bg-purple-500/10',
    link: 'https://productbyte.com',
  },
  {
    title: 'Auguro',
    description: 'Website monitoring that opens each site in real Chromium and checks it actually renders, with screenshots, page source and cross-continent confirmation before alerting.',
    type: 'SaaS Product',
    icon: Activity,
    color: 'text-cyan-500',
    bg: 'bg-cyan-500/10',
    link: 'https://auguro.dev',
  },
  {
    title: 'Pickr.date',
    description: 'A date scheduling tool that helps groups find the perfect time to meet. Built entirely on Cloudflare Workers with Durable Objects and D1 storage.',
    type: 'Cloudflare Workers',
    icon: Calendar,
    color: 'text-orange-500',
    bg: 'bg-orange-500/10',
    link: 'https://pickr.date',
  },
  {
    title: 'Adem je Beter',
    description: 'A 2-minute breathing and recovery self-scan that returns a personal VitaliteitsLabel (A-G). Runs entirely in the browser, no account needed.',
    type: 'Web App & Landing Page',
    icon: Wind,
    color: 'text-teal-500',
    bg: 'bg-teal-500/10',
    link: 'https://ademjebeter.nl',
  },
  {
    title: 'Michel Kreder Coaching',
    description: 'WordPress website setup, hosting, and maintenance for a professional coaching business.',
    type: 'WordPress Setup & Hosting',
    icon: Globe,
    color: 'text-blue-500',
    bg: 'bg-blue-500/10',
    link: 'https://michelkredercoaching.nl',
  },
  {
    title: 'Schoonheidssalon Anina',
    description: 'WordPress website setup, hosting, and maintenance for a beauty salon business.',
    type: 'WordPress Setup & Hosting',
    icon: Globe,
    color: 'text-pink-500',
    bg: 'bg-pink-500/10',
    link: 'https://salonanina.nl',
  },
  {
    title: 'Kreder',
    description: 'Website hosting for kreder.nl and office workplace IT support.',
    type: 'Hosting & IT Support',
    icon: Server,
    color: 'text-emerald-500',
    bg: 'bg-emerald-500/10',
    link: 'https://kreder.nl',
  },
];

export function Projects() {
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
      <div className="text-center mb-16 space-y-4">
        <h2 className="text-3xl md:text-5xl font-bold tracking-tight">My Projects</h2>
        <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
          A selection of projects I've been working on.
        </p>
      </div>

      <div className="relative">
        <Carousel
          opts={{
            align: 'start',
            loop: true,
          }}
          className="w-full"
        >
          <CarouselContent className="-ml-2 md:-ml-4">
            {projects.map((project) => {
              const Icon = project.icon;
              return (
                <CarouselItem key={project.title} className="pl-2 md:pl-4 md:basis-1/2 lg:basis-1/3">
                  <div>
                    <Card className="h-full border-border/50 hover:border-primary/30 transition-all duration-300 group overflow-hidden">
                      <CardHeader>
                        <div className="flex items-start justify-between mb-4">
                          <div className={`${project.bg} w-12 h-12 rounded-xl flex items-center justify-center transition-transform group-hover:scale-110`}>
                            <Icon className={`w-6 h-6 ${project.color}`} />
                          </div>
                          {project.status && (
                            <Badge variant="secondary" className="text-xs">
                              {project.status}
                            </Badge>
                          )}
                        </div>
                        <CardTitle className="text-xl mb-2">{project.title}</CardTitle>
                        <CardDescription className="text-sm">{project.type}</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {project.description}
                        </p>
                        {project.link && (
                          <a
                            href={project.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 text-sm text-primary hover:text-primary/80 font-medium transition-colors group/link"
                          >
                            Visit Site
                            <ExternalLink className="w-3 h-3 group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5 transition-transform" />
                          </a>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                </CarouselItem>
              );
            })}
          </CarouselContent>
          <CarouselPrevious className="hidden md:flex" />
          <CarouselNext className="hidden md:flex" />
        </Carousel>
      </div>
    </div>
  );
}

